import React, { FC, useState } from 'react';
import styled, { css } from 'styled-components';
import { darken } from 'polished';

import { MOBILE_BREAKPOINT_PX } from '../../shared/helpers';
import {
	pageCloseClickDecorator,
	PageCloseClickOptions,
	actionDecorator,
	ActionOptions,
} from '../../shared/helpers.ui';
import { ReactComponent as LeftArrowIcon } from '../../shared/icons/arrow-left-solid.svg';
import { ReactComponent as TimesIcon } from '../../shared/icons/times-solid.svg';
import { ReactComponent as MenuExpand } from '../../shared/icons/ic-menu-expand.svg';
import { ReactComponent as MenuContract } from '../../shared/icons/ic-menu-contract.svg';
import { useAppStore } from '../../state/store';
import { Icon, Input, Stack } from '../globals';
import { useIsMobile } from '../../hooks/useLayoutCheck';
import { Button, IconButton } from '../widgets/button';

// Icons
const MenuPageContainer = styled.div`
	height: 100%;
	display: flex;
	flex-direction: column;
	align-items: stretch;
`;

const Title = styled.h1`
	font: ${({ theme }) => theme.fonts.large200bold};
	margin-top: 0;
	margin-bottom: 0;
	display: flex;
	align-items: center;
	position: relative;
	text-wrap: balance;

	@media screen and (max-width: ${MOBILE_BREAKPOINT_PX}) {
		font: ${({ theme }) => theme.fonts.large100bold};
	}
	${Icon} {
		color: ${({ theme }) => theme.colors.iconSecondary};
		@media (hover) {
			&:hover {
				color: ${({ theme }) => theme.colors.iconSecondaryState};
			}
		}
	}
`;

const BackIcon = styled(Icon)`
	margin-inline-end: 20px;
	cursor: pointer;
	color: ${({ theme }) => theme.colors.iconMain};

	@media (hover) {
		&:hover {
			color: ${({ theme }) => theme.colors.primary};
		}
	}
`;

const ExpandIcon = styled(Icon)``;

const CloseIcon = styled(Icon)``;

const TitleIcon = styled.div`
	&:not(:first-child) {
		border-inline-start: 1px solid ${({ theme }) => theme.colors.borders};
		padding-inline-start: 10px;
		margin-inline-start: 10px;
	}
`;

const TitleIcons = styled.div`
	display: flex;
	margin-inline-start: auto;
	transform: translateX(4px);

	${ExpandIcon}, ${CloseIcon} {
		width: 20px;
		height: 20px;
		cursor: pointer;
		color: ${({ theme }) => theme.colors.iconSecondary};

		@media (hover) {
			&:hover {
				color: ${({ theme }) => theme.colors.iconSecondaryState};
			}
		}
	}
`;

export const MenuPageHeader = styled.div<{ $titleSmallerPad?: boolean }>`
	padding: 12px ${({ theme }) => theme.spacing.horizontalPadder} 10px;
	color: ${({ theme }) => theme.colors.textSecondary};

	@media screen and (max-width: ${MOBILE_BREAKPOINT_PX}) {
		padding: 8px ${(props) => (props.$titleSmallerPad ? '12px' : props.theme.spacing.horizontalPadderMobile)} 10px;
	}
`;

const MenuFooter = styled.div<{ $backgroundColor?: string; $smallerPad?: boolean }>`
	padding: 8px ${({ theme }) => theme.spacing.horizontalPadder} 12px;
	background-color: ${(props) =>
		props.$backgroundColor ? props.$backgroundColor : props.theme.colors.backgroundMain};
	@media screen and (max-width: ${MOBILE_BREAKPOINT_PX}) {
		padding: 8px ${(props) => (props.$smallerPad ? '12px' : props.theme.spacing.horizontalPadderMobile)} 20px;
	}
`;

const MenuPageContent = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	height: 100%;
	min-height: 0;
	padding-bottom: 1px;
	padding-top: 16px;
	overflow: auto;
	border-top-left-radius: calc(${({ theme }) => theme.measures.borderRadius} * 6);
	border-top-right-radius: calc(${({ theme }) => theme.measures.borderRadius} * 6);
	overflow-x: hidden;
	position: relative;
	background-color: ${({ theme }) => theme.colors.backgroundMain};
	@media screen and (max-width: ${MOBILE_BREAKPOINT_PX}) {
		padding-top: 24px;
	}
`;

export const HorizontalPadder = styled.div<{ $disableGutters?: boolean }>`
	display: flex;
	flex-direction: column;
	${(props) =>
		!props.$disableGutters &&
		css`
			padding-inline: ${props.theme.spacing.horizontalPadder};
			@media screen and (max-width: ${MOBILE_BREAKPOINT_PX}) {
				padding-inline: ${props.theme.spacing.horizontalPadderMobile};
			}
		`}
`;

// const ToolsList = styled(List)`
// 	min-width: 250px;
// `;

export const MenuInnerWrapper = styled.div`
	padding-bottom: 24px;
`;

export type MenuPageProps = {
	title?: React.ReactNode;
	header?: React.ReactNode;
	children?: React.ReactNode;
	footer?: React.ReactNode;
	showBackButton?: boolean;
	onBackButtonClick?: (e: ActionOptions) => void;
	onCloseClick?: (e: PageCloseClickOptions) => void;
	expanded?: boolean;
	onExpand?: () => void;
	footerBackgroundColor?: string;
	footerSmallerPad?: boolean;
	titleSmallerPad?: boolean;
	hideCloseButton?: boolean;
};

export const MenuPage: FC<MenuPageProps> = ({
	title,
	header,
	children,
	footer,
	showBackButton,
	onCloseClick,
	onBackButtonClick,
	expanded,
	onExpand,
	footerBackgroundColor,
	footerSmallerPad,
	titleSmallerPad,
	hideCloseButton,
}) => {
	const isMobile = useIsMobile();
	const [setMenuPage, menuPage, menuPageHistory, setSelectedItemGuid, goBackMenuPage] = useAppStore((x) => [
		x.setMenuPage,
		x.menuPage,
		x.menuPageHistory,
		x.setSelectedItemGuid,
		x.goBackMenuPage,
	]);

	const handleBackClick = () => {
		const { isDefaultPrevented } = actionDecorator(onBackButtonClick);
		if (isDefaultPrevented) return;
		if (menuPageHistory.length > 1) goBackMenuPage();
		else setMenuPage({ page: 'design-elements' });
	};

	const handleCloseClick = async () => {
		const { preventDefaultCloseBehaviour } = pageCloseClickDecorator(onCloseClick);
		if (preventDefaultCloseBehaviour) return;
		setSelectedItemGuid(null, {
			// If we have at last one item and the page is not design elements, then return to design elements
			// Requested this behaviour by Carlo
			preventPageChange: true,
		});
		setMenuPage({
			page: menuPage.page !== 'design-elements' && !isMobile ? 'design-elements' : 'none',
		});
	};

	return (
		<MenuPageContainer>
			<MenuPageHeader $titleSmallerPad={titleSmallerPad}>
				<Title>
					{!isMobile && showBackButton && (
						<BackIcon onClick={handleBackClick}>
							<LeftArrowIcon />
						</BackIcon>
					)}
					{title}
					<TitleIcons>
						{!isMobile && onExpand && (
							<TitleIcon>
								<ExpandIcon onClick={onExpand}>
									{expanded ? <MenuContract /> : <MenuExpand />}
								</ExpandIcon>
							</TitleIcon>
						)}
						{hideCloseButton !== true && (
							<TitleIcon>
								<CloseIcon onClick={handleCloseClick}>
									<TimesIcon />
								</CloseIcon>
							</TitleIcon>
						)}
					</TitleIcons>
				</Title>
				{header}
			</MenuPageHeader>
			<MenuPageContent>{children}</MenuPageContent>
			{footer && (
				<MenuFooter $backgroundColor={footerBackgroundColor} $smallerPad={footerSmallerPad}>
					{footer}
				</MenuFooter>
			)}
		</MenuPageContainer>
	);
};

const SectionContainer = styled.div<{ $noLabelMinWidth?: boolean; $noMaxHeight?: boolean }>`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 8px;
	border-bottom: 1px solid ${({ theme }) => theme.colors.borders};
	padding: 3px 0;
	font-size: 13px;
	min-height: 40px;
	
	${(props) =>
			props.$noMaxHeight &&
			css`
				height: 40px;
				min-height: max-content;
				padding: 6px 0;
			`};

	> label {
		margin-right: auto;
		min-width: 45%;
		${(props) =>
			props.$noLabelMinWidth &&
			css`
				min-width: unset;
			`};
	}

	> div {
		flex: 1;
		display: flex;
		justify-content: flex-end;
		align-items: center;
	}

	${Input} {
		border-color: transparent transparent ${({ theme }) => theme.colors.borders};
		text-align: end;
		padding: 7px 10px;
		min-height: 30px;
		border-radius: 0;

		&:focus {
			border-color: ${({ theme }) => theme.colors.primary};
			border-radius: ${({ theme }) => theme.measures.borderRadius};
		}
	}
`;

export const SectionGroup = styled.div`
	${SectionContainer}:first-child:not(:only-child) {
		border-bottom: 0 none;
	}
	${SectionContainer}:last-child:not(:only-child) {
		border-top: 0 none;
	}
	${SectionContainer}:not(:first-child):not(:last-child) {
		border: 0 none;
		padding-top: 3px;
		padding-bottom: 3px;
		min-height: 40px;
	}
`;

type SectionProps = {
	label: string;
	children: React.ReactNode;
	noLabelMinWidth?: boolean;
	noMaxHeight?: boolean;
};
const Section: FC<SectionProps> = ({ label, noLabelMinWidth, noMaxHeight, children }) => (
	<SectionContainer $noLabelMinWidth={noLabelMinWidth} $noMaxHeight={noMaxHeight} >
		<label>{label}</label>
		<div>{children}</div>
	</SectionContainer>
);

export const SubSection = styled.div`
	background-color: ${(props) => darken(0.035, props.theme.colors.backgroundMain)};
	${SectionContainer} {
		padding-left: 16px;
		padding-right: 16px;
	}
`;

export const SectionPageItem = styled.div`
	&:last-child {
		padding-bottom: 30px;
	}
`;

export const SectionPageItemTitle = styled.header`
	h3 {
		margin-top: 0;
		margin-bottom: 10px;
		font: ${({ theme }) => theme.fonts.large200bold};
	}
`;

export const SectionPageItemIntro = styled.div`
	display: flex;
	justify-content: space-between;
	padding-bottom: 8px;
	border-bottom: 1px solid ${({ theme }) => theme.colors.borders};

	h3 {
		margin-top: 12px;
		margin-bottom: 12px;
		font: ${({ theme }) => theme.fonts.large200bold};
	}
`;

export const SectionPagePreviewWrapper = styled.div`
	width: 42px;
	height: 48px;
`;

export const SectionPagePreviewSize = styled.div`
	margin-inline: 15px auto;
	align-self: center;
	font-size: 12px;
	p {
		font-size: 14px;
		margin: 2px 0 0;
	}
`;

 const SectionPagePreviewImg = styled.img<{ $error: boolean }>`
	width: 42px;
	height: 48px;
	object-fit: contain;
	object-position: left center;
	font-size: 0.1px;

	${(props) =>
		props.$error && css`
			visibility: hidden;
		`}
`;

export const SectionPagePreview : FC<React.ImgHTMLAttributes<HTMLImageElement>> = (props) => {
	const [error, hasError] = useState(false);
	return <SectionPagePreviewImg
		{
			...props
		}
		onError={(e) => {
			hasError(true);
			props.onError?.(e);
		}}
		onLoad={(e) => {
			hasError(false);
			props.onLoad?.(e);
		}}
		$error={error}
	/>;
}

export const SectionPageItemActions = styled.div`
	display: flex;
`;

export const SectionPageItemButton = styled.button`
	display: flex;
	justify-content: center;
	align-items: center;
	max-width: 32px;
	min-width: 32px;
	padding: 4px;
	border: none;
	background: transparent;
	color: ${({ theme }) => theme.colors.iconMain};
	cursor: pointer;

	@media (hover) {
		&:hover {
			color: ${({ theme }) => theme.colors.primary};
		}
	}
`;

// #region text and textart shared components

export const TextArtTypesContainerDesktop = styled.div<{ $fromTemplate?: boolean }>`
	display: grid;
	grid-template-columns: ${(props) => (props.$fromTemplate ? 'repeat(4, 1fr)' : 'repeat(5, 1fr)')};
	grid-template-rows: repeat(2, auto);
	gap: 8px;
	padding: 4px 0 8px 0;
	cursor: pointer;
`;

export const TextArtType = styled(Button)<{ $selected: boolean }>`
	border-radius: calc(${({ theme }) => theme.measures.borderRadius} * 2);
	align-items: end;
	padding: 5px;
	border: 1px solid ${(props) => (props.$selected ? props.theme.colors.primary : props.theme.colors.borders)};
	${(props) => (props.$selected ? 'box-shadow: 0 0 0 1px ' + props.theme.colors.primary : '')};
	width: 100%;
	height: 40px;
	object-fit: contain;
	@media (hover) {
		&:hover {
			border-color: ${({ theme }) => theme.colors.primary};
		}
	}
`;

export const PlainTextType = styled(Button)<{ $selected: boolean }>`
	align-items: center;
	border-radius: calc(${({ theme }) => theme.measures.borderRadius} * 2);
	padding: 5px;
	border: 1px solid ${(props) => (props.$selected ? props.theme.colors.primary : props.theme.colors.borders)};
	${(props) => (props.$selected ? 'box-shadow: 0 0 0 1px ' + props.theme.colors.primary : '')};
	height: 40px;
	@media (hover) {
		&:hover {
			border-color: ${({ theme }) => theme.colors.primary};
		}
	}
`;

export const FontPreview = styled.button`
	display: flex;
	align-items: center;
	cursor: pointer;
	min-width: 0;
	border: 0;
	background: none;

	@media (hover) {
		&:hover {
			img {
				opacity: 0.7;
			}
			${Icon} {
				color: ${({ theme }) => theme.colors.primary};
			}
		}
	}

	&:disabled {
		opacity: 0.5;
		cursor: default;
	}

	img {
		flex: 1;
		object-fit: contain;
		min-width: 0;
		max-width: 100%;
		height: 23px;
	}

	${Icon} {
		color: ${({ theme }) => theme.colors.iconMain};
		width: 18px;
		height: 18px;
		margin-left: 10px;
	}
`;

export const TextFeaturesContainer = styled(HorizontalPadder)`
	margin-top: 12px;
	padding-bottom: 20px;
	height: 100%;
	overflow-y: auto;

	@supports (scrollbar-gutter: stable) {
		scrollbar-gutter: stable;
		padding-inline-end: ${({ theme }) => theme.spacing.horizontalPadderNumber - 12}px;
		/* fix for firefox only */
		@-moz-document url-prefix() {
			& {
				padding-inline-end: ${({ theme }) => theme.spacing.horizontalPadder};
			}
		}
	}
`;

export const TextTypeButtonImagePreview = styled.img`
	width: 100%;
	height: 100%;
`;

export const TextContentStack = styled(Stack)`
	padding: 0 20px;
	align-items: center;

	${IconButton} {
		padding-inline: 3px;
		border-radius: ${({ theme }) => theme.measures.borderRadius};
	}
`;

// #endregion

export const SectionPageItemControls = styled.div``;

export default Section;
